import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  HandHelping,
  Heart,
  Send,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PublicHeader } from "@/components/PublicHeader";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

function relativeTime(iso, lang, fallback) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const delta = Math.max(0, now - then);
    const min = Math.floor(delta / 60000);
    if (min < 1) return fallback;
    if (min < 60) return lang === "es" ? `hace ${min} min` : `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return lang === "es" ? `hace ${hr} h` : `${hr} h ago`;
    return fmtDate(iso, lang);
  } catch (_) {
    return fmtDate(iso, lang);
  }
}

export default function PrayersPage() {
  const { t, lang } = useI18n();
  const { member } = useMember();
  const [loading, setLoading] = useState(true);
  const [prayers, setPrayers] = useState([]);

  const [authorName, setAuthorName] = useState(member?.full_name || "");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({}); // id -> bool

  useEffect(() => {
    setAuthorName((cur) => (cur && cur.trim() ? cur : (member?.full_name || "")));
  }, [member]);

  const reload = async () => {
    try {
      const { data } = await api.get("/prayers");
      setPrayers(Array.isArray(data) ? data : []);
    } catch (_) {
      setPrayers([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!authorName.trim() || !body.trim()) {
      toast.error(t.prayer.requiredAll);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/prayers", {
        author_name: authorName.trim(),
        body: body.trim(),
        member_id: member?.id || null,
        language: lang,
      });
      setBody("");
      toast.success(t.prayer.success);
      await reload();
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  const onPray = async (pid) => {
    // Optimistic: bump count locally first, then sync from server response.
    setPrayers((cur) => cur.map((p) => p.id === pid ? { ...p, prayed_count: (p.prayed_count || 0) + 1, _just_prayed: true } : p));
    try {
      const { data } = await api.post(`/prayers/${pid}/pray`);
      setPrayers((cur) => cur.map((p) => p.id === pid ? { ...data, _just_prayed: true } : p));
    } catch (_) {
      // Revert on error
      setPrayers((cur) => cur.map((p) => p.id === pid ? { ...p, prayed_count: Math.max(0, (p.prayed_count || 0) - 1), _just_prayed: false } : p));
      toast.error(t.common.error);
    }
  };

  return (
    <div>
      <PublicHeader />
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="px-4 pt-4 space-y-4"
      >
        <div className="flex items-start gap-2 px-1">
          <HandHelping className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h1 className="font-serif-display text-2xl font-semibold tracking-tight">{t.prayer.pageTitle}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{t.prayer.pageSubtitle}</p>
          </div>
        </div>

        {/* Submit form */}
        <Card className="rounded-2xl shadow-sm ring-1 ring-black/5">
          <CardContent className="p-4">
            <form className="space-y-3" onSubmit={onSubmit} data-testid="prayer-submit-form">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-primary" />
                <p className="font-serif-display text-base font-semibold">{t.prayer.submitTitle}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t.prayer.yourName}</Label>
                <Input
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  maxLength={80}
                  data-testid="prayer-author-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{t.prayer.yourRequest}</Label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t.prayer.yourRequestPlaceholder}
                  rows={4}
                  maxLength={1500}
                  data-testid="prayer-body"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !authorName.trim() || !body.trim()}
                className="w-full sm:w-auto"
                data-testid="prayer-submit-button"
              >
                {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                {submitting ? t.prayer.submitting : t.prayer.submit}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full rounded-2xl" />
            ))}
          </div>
        ) : prayers.length === 0 ? (
          <Card className="rounded-2xl border-dashed bg-white/60">
            <CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="prayer-empty">
              {t.prayer.emptyList}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="prayer-list">
            {prayers.map((p) => (
              <PrayerCard
                key={p.id}
                prayer={p}
                expanded={!!expanded[p.id]}
                onToggle={() => setExpanded((cur) => ({ ...cur, [p.id]: !cur[p.id] }))}
                onPray={() => onPray(p.id)}
                onCommented={(updated) => {
                  setPrayers((cur) => cur.map((x) => x.id === p.id ? updated : x));
                  setExpanded((cur) => ({ ...cur, [p.id]: true }));
                }}
                lang={lang}
                t={t}
                memberName={member?.full_name || ""}
                memberId={member?.id || null}
              />
            ))}
          </div>
        )}
        <div className="h-2" />
      </motion.div>
    </div>
  );
}

function PrayerCard({ prayer, expanded, onToggle, onPray, onCommented, lang, t, memberName, memberId }) {
  const count = prayer.prayed_count || 0;
  const countLabel = useMemo(() => {
    if (count === 1) return t.prayer.prayedCountOne;
    return (t.prayer.prayedCount || "{n} praying").replace("{n}", String(count));
  }, [count, t.prayer]);
  const commentCount = (prayer.comments || []).length;
  const when = relativeTime(prayer.created_at, lang, t.prayer.momentsAgo);

  return (
    <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm" data-testid={`prayer-card-${prayer.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-serif-display text-base font-semibold" data-testid={`prayer-author-${prayer.id}`}>{prayer.author_name}</p>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{when}</p>
          </div>
          {count > 0 && (
            <Badge variant="secondary" className="shrink-0 gap-1 text-[11px]" data-testid={`prayer-count-${prayer.id}`}>
              <Heart className="h-3 w-3 fill-current" />
              {count}
            </Badge>
          )}
        </div>
        <p className="whitespace-pre-wrap text-sm text-foreground/90" data-testid={`prayer-body-${prayer.id}`}>{prayer.body}</p>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant={prayer._just_prayed ? "default" : "outline"}
            onClick={onPray}
            className="gap-1"
            data-testid={`prayer-pray-${prayer.id}`}
          >
            <Heart className={`h-4 w-4 ${prayer._just_prayed ? "fill-current" : ""}`} />
            {t.prayer.prayingButtonShort}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onToggle}
            className="gap-1 text-muted-foreground"
            data-testid={`prayer-toggle-comments-${prayer.id}`}
          >
            <MessageCircle className="h-4 w-4" />
            {commentCount > 0
              ? (t.admin?.prayersPage?.commentsLabel || "{n} comments").replace("{n}", String(commentCount))
              : t.prayer.commentsLabel}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {expanded && (
          <div className="space-y-3 pt-1">
            <Separator />
            {commentCount === 0 ? (
              <p className="text-xs text-muted-foreground italic">{t.prayer.noComments}</p>
            ) : (
              <div className="space-y-2" data-testid={`prayer-comments-${prayer.id}`}>
                {prayer.comments.map((c) => (
                  <div key={c.id} className="rounded-xl bg-muted/50 p-2.5" data-testid={`prayer-comment-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold">{c.author_name}</p>
                      <p className="text-[10px] text-muted-foreground">{relativeTime(c.created_at, lang, t.prayer.momentsAgo)}</p>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{c.body}</p>
                  </div>
                ))}
              </div>
            )}
            <CommentInput
              prayerId={prayer.id}
              memberName={memberName}
              memberId={memberId}
              onPosted={onCommented}
              t={t}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CommentInput({ prayerId, memberName, memberId, onPosted, t }) {
  const [name, setName] = useState(memberName || "");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setName((cur) => cur || memberName || ""); }, [memberName]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/prayers/${prayerId}/comments`, {
        author_name: name.trim(),
        body: body.trim(),
        member_id: memberId || null,
      });
      setBody("");
      onPosted?.(data);
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-2" data-testid={`prayer-comment-form-${prayerId}`}>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t.prayer.yourName}
        className="h-9 text-sm"
        maxLength={80}
        data-testid={`prayer-comment-author-${prayerId}`}
      />
      <div className="flex items-start gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t.prayer.commentInputPlaceholder}
          rows={2}
          maxLength={800}
          className="text-sm"
          data-testid={`prayer-comment-body-${prayerId}`}
        />
        <Button
          type="submit"
          size="sm"
          disabled={submitting || !name.trim() || !body.trim()}
          data-testid={`prayer-comment-submit-${prayerId}`}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </form>
  );
}
